import React from "react";
import Highcharts from "highcharts";
import exporting from "highcharts/modules/exporting";
import offlineExporting from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";

// Initialize Highcharts modules
exporting(Highcharts);
offlineExporting(Highcharts);

// Utility imports (adjust types as defined in your project)
import { IHighChartsDataseries, formatLabelNumber } from "../utility";

interface ChartComboProps {
  title: string;
  subTitle: string;
  // Expect exactly two series – one for each field used in the combo chart.
  dataSeries: IHighChartsDataseries[];
  xAxisCategoryLabels: string[];
  formatFunction?: () => any; 
}

const ChartCombo: React.FC<ChartComboProps> = ({
  title,
  subTitle,
  dataSeries,
  xAxisCategoryLabels,
}) => {
  // Helper functions to get prefix and suffix from the first data point in a series.
  const getPrefix = (series: IHighChartsDataseries): string => {
    if (series.data && series.data.length > 0 && typeof series.data[0] === "object") {
      return (series.data[0] as any).prefix || "";
    }
    return "";
  };

  const getSuffix = (series: IHighChartsDataseries): string => {
    if (series.data && series.data.length > 0 && typeof series.data[0] === "object") {
      return (series.data[0] as any).suffix || "";
    }
    return "";
  };

  // Tooltip formatter replicates legacy behavior.
  const tooltipFormatter = function (this: any) {
    return (
      "<b>" +
      this.key +
      "</b><br/><span style=\"color:" +
      this.series.color +
      "\">" +
      this.series.name +
      "</span>: " +
      this.point.displayVal +
      "<br/>"
    );
  };

  // Transform the series:
  // - First series: column type, yAxis index 0.
  // - Second series: spline type, yAxis index 1.
  // Also set a border color to mimic legacy settings.
  const transformedSeries = dataSeries.map((series, index) => ({
    ...series,
    type: index === 0 ? "column" : "spline",
    yAxis: index === 0 ? 0 : 1,
    borderColor: "#000",
  }));

  // Create two yAxis objects using legacy formatting logic,
  // now fetching prefix and suffix from the data points array.
  const yAxisObjectArray = [
    {
      gridLineColor: "#555",
      labels: {
        formatter: function () {
          return formatLabelNumber(
            this.value,
            true,
            getPrefix(dataSeries[0]),
            getSuffix(dataSeries[0])
          );
        },
        style: {
          color: Highcharts.getOptions().colors[0],
        },
      },
      title: {
        text: dataSeries[0].name,
        style: {
          color: Highcharts.getOptions().colors[0],
        },
      },
      min: 0,
    },
    {
      gridLineColor: "#555",
      labels: {
        formatter: function () {
          return formatLabelNumber(
            this.value,
            true,
            getPrefix(dataSeries[1]),
            getSuffix(dataSeries[1])
          );
        },
        style: {
          color: Highcharts.getOptions().colors[1],
        },
      },
      title: {
        text: dataSeries[1].name,
        style: {
          color: Highcharts.getOptions().colors[1],
        },
      },
      opposite: true,
      min: 0,
    },
  ];

  const options = {
    exporting: {
      buttons: {
        contextButton: {
          menuItems: ["downloadPNG", "downloadJPEG", "downloadPDF", "downloadSVG"],
        },
      },
    },
    credits: { enabled: false },
    chart: {
      height: 600,
      renderTo:"chartContainer",
      // No global type is set so each series’ type is honored.
    },
    title: { text: title },
    subtitle: { text: subTitle },
    legend: { layout: "vertical" },
    xAxis: {
      categories: xAxisCategoryLabels,
      crosshair: true,
      labels: { enabled: true },
    },
    yAxis: yAxisObjectArray,
    series: transformedSeries,
    tooltip: { formatter: tooltipFormatter, shared: false },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          formatter: function () {
            return formatLabelNumber(
              this.y,
              true,
              this.point.prefix,
              this.point.suffix
            );
          },
          style: { textShadow: "" },
        },
      },
      column: { pointPadding: 0.2, borderWidth: 0 },
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default ChartCombo;
